/**
 * AuthProvider — React context providing auth state across all Plerity apps.
 *
 * Three-tier auth resolution on mount:
 * 1. Read `animas_auth` JWT cookie directly (fastest — no network)
 * 2. Call Portal /api/auth/me cross-origin (uses hub_session cookie)
 * 3. POST /api/auth/unified-refresh (uses 7-day animas_refresh cookie)
 *
 * This ensures shells stay authenticated even when the 15-min access token
 * and 30-min session cookie have both expired, as long as the 7-day refresh
 * token is still valid.
 *
 * Every refresh (Tier 2's background refresh, Tier 3, the expiry timer, the
 * 60s retry, a consumer's `refresh()`) goes through ONE single-flight
 * `createAuthClient` refresh, so concurrent paths share a single network
 * request instead of racing on the rotating refresh cookie. The same client
 * syncs tabs: a `signed-out` message from another tab sets `user` to null.
 *
 * @example
 * ```tsx
 * import { AuthProvider } from '@plerity/auth/react';
 *
 * <AuthProvider
 *   portalApiUrl="https://app.animasmarketing.com"
 *   loginUrl="https://app.animasmarketing.com/login"
 * >
 *   <App />
 * </AuthProvider>
 * ```
 */

import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type AuthClient, createAuthClient } from "../client/index.js";
import { decodeUnifiedTokenUnsafe, getUnifiedTokenExpiry } from "../jwt.js";
import type { AuthUser, UnifiedAuthConfig } from "../types.js";
import { isE2EAuthBypass } from "./e2e-bypass.js";

// ============================================================================
// Internal response shapes (portal auth API contract)
// ============================================================================
//
// Under the portal's typecheck (@cloudflare/workers-types is in scope),
// `Response.json()` returns `Promise<unknown>` rather than `Promise<any>`.
// These interfaces describe the known portal auth API bodies so property
// access type-checks without any runtime change.

/** User object inside the /api/auth/me success response. */
interface MeResponseUser {
  id: number;
  email: string;
  display_name?: string | null;
  org_id?: number | null;
  org_slug?: string | null;
  role?: string | null;
  entitlements?: string[] | null;
  is_super_admin?: boolean;
  brand_color?: string | null;
}

/** Shape of the /api/auth/me response body. */
interface MeResponse {
  success?: boolean;
  user?: MeResponseUser | null;
}

/** Shape of an error response body (e.g. /api/auth/org-switch failure). */
interface ErrorResponse {
  error?: string;
}

// ============================================================================
// Context
// ============================================================================

export interface AuthContextValue {
  /** Current authenticated user, or null if not authenticated */
  user: AuthUser | null;
  /** Whether auth state is still being resolved */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Switch to a different org (re-issues JWT) */
  switchOrg: (orgId: number) => Promise<void>;
  /** Sign out — clears cookies and redirects to Portal logout */
  signOut: () => void;
  /** Force a token refresh */
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
  config: UnifiedAuthConfig;
}

/** Synthetic super admin served in E2E bypass mode (see e2e-bypass.ts). */
const E2E_BYPASS_USER: AuthUser = {
  userId: 0,
  email: "e2e-agent@test.local",
  name: "E2E Agent",
  orgId: null,
  orgSlug: null,
  role: "admin",
  entitlements: [],
  isSuperAdmin: true,
};

/** Delay before retrying a refresh that failed transiently (network / 429 / 5xx). */
const REFRESH_RETRY_MS = 60_000;

/** A decoded cookie user carries its token expiry (epoch seconds) alongside the claims. */
type UserWithExpiry = AuthUser & { _tokenExpiry?: number };

/**
 * Read the animas_auth cookie value.
 *
 * The cookie is NOT httpOnly (fixed 2026-04-01) so JS can read it directly.
 * Returns the raw JWT string, or null if absent.
 */
function readAuthCookie(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith("animas_auth=")) {
      return decodeURIComponent(trimmed.slice("animas_auth=".length));
    }
  }
  return null;
}

export function AuthProvider({ children, config }: AuthProviderProps) {
  // E2E bypass — resolved from the BUILD-TIME `__E2E_AUTH_BYPASS__` constant
  // (false + elided in prod), never a runtime-settable window global. See
  // e2e-bypass.ts (S02 F-5).
  const bypassAtInit = isE2EAuthBypass();

  const [user, setUser] = useState<AuthUser | null>(
    // In E2E bypass mode, provide a synthetic super admin user immediately
    // so AuthGuard and useAuth() consumers see an authenticated state.
    bypassAtInit ? E2E_BYPASS_USER : null
  );
  const [isLoading, setIsLoading] = useState(!bypassAtInit);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRefreshRef = useRef<((expiry?: number) => void) | null>(null);
  const configRef = useRef(config);
  configRef.current = config;
  const clientRef = useRef<AuthClient | null>(null);

  const refreshThreshold = config.refreshThreshold ?? 180; // 3 minutes

  /** Helper: decode animas_auth cookie into AuthUser with _tokenExpiry attached */
  const tryDecodeCookie = useCallback((): UserWithExpiry | null => {
    const cookieToken = readAuthCookie();
    if (!cookieToken) return null;
    const decoded = decodeUnifiedTokenUnsafe(cookieToken);
    if (!decoded) return null;
    const expiry = getUnifiedTokenExpiry(cookieToken);
    (decoded as UserWithExpiry)._tokenExpiry = expiry ?? undefined;
    return decoded as UserWithExpiry;
  }, []);

  const clearTimers = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    refreshTimerRef.current = null;
    retryTimerRef.current = null;
  }, []);

  /**
   * The single auth client behind every refresh path. Created lazily (a child
   * effect can run before this component's own effects) and disposed on unmount
   * so the cross-tab channel never leaks. The client is the source of truth for
   * `user`; the subscription mirrors it into React state.
   */
  const getClient = useCallback((): AuthClient => {
    if (clientRef.current) return clientRef.current;
    const client = createAuthClient({
      refreshUrl: () => `${configRef.current.portalApiUrl}/api/auth/unified-refresh`,
      logoutUrl: () => configRef.current.logoutUrl,
      initialUser: bypassAtInit ? E2E_BYPASS_USER : null,
      // Cookie mode: the refresh response Set-Cookie wrote a fresh animas_auth;
      // document.cookie reflects it synchronously. Undecodable → keep the user.
      resolveUser: () => tryDecodeCookie() ?? undefined,
      // Whether a rejection signs the user out is decided per call site (refreshOnce).
      keepUserOnRejected: true,
      onUnauthenticated: () => client.setUser(null),
    });
    client.subscribe((next, event) => {
      setUser(next);
      if (next === null) {
        clearTimers();
      } else if (event === "remote-refreshed") {
        // Another tab rotated the cookie — track the new token's expiry.
        scheduleRefreshRef.current?.((next as UserWithExpiry)._tokenExpiry);
      }
    });
    clientRef.current = client;
    return client;
  }, [bypassAtInit, tryDecodeCookie, clearTimers]);

  /** Helper: call /api/auth/me and parse response into AuthUser */
  const tryMeEndpoint = useCallback(async (): Promise<AuthUser | null> => {
    // ssrf-ok: browser-side client; URL is configRef.portalApiUrl (own portal API base) + fixed route
    const res = await fetch(`${configRef.current.portalApiUrl}/api/auth/me`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as MeResponse;
    if (!data.success || !data.user) return null;
    return {
      userId: data.user.id,
      email: data.user.email,
      name: data.user.display_name ?? data.user.email.split("@")[0],
      orgId: data.user.org_id ?? null,
      orgSlug: data.user.org_slug ?? null,
      role: data.user.role ?? "member",
      entitlements: data.user.entitlements ?? [],
      isSuperAdmin: Boolean(data.user.is_super_admin),
      brandColor: data.user.brand_color ?? null,
    };
  }, []);

  /**
   * One silent refresh through the shared single-flight client.
   *
   * - refreshed + readable cookie → new user (set by the client), schedule the next refresh
   * - unavailable (network / 429 / 5xx) → keep the user and retry in 60s
   * - rejected (401/403) → credentials are cleared by the client
   *
   * `signOutOnFailure` selects the legacy per-call-site policy for the cases
   * that cannot be recovered: the timer / consumer path signs the user out when
   * the refresh is rejected, or succeeds without leaving a readable cookie; the
   * Tier-2 background path never does (the hub_session user stays authenticated
   * for the UI even though product API calls will 401 until re-login).
   */
  const refreshOnce = useCallback(
    async (signOutOnFailure: boolean): Promise<void> => {
      const client = getClient();
      const outcome = await client.refresh();
      if (outcome.status === "refreshed") {
        if (outcome.userResolved && outcome.user) {
          scheduleRefreshRef.current?.((outcome.user as UserWithExpiry)._tokenExpiry);
        } else if (signOutOnFailure) {
          client.setUser(null);
        }
      } else if (outcome.status === "rejected") {
        if (signOutOnFailure) client.setUser(null);
      } else {
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          void refreshOnce(signOutOnFailure);
        }, REFRESH_RETRY_MS);
      }
    },
    [getClient]
  );

  /** Silent token refresh — the scheduleRefresh timer, the 60s retry and consumers (`useAuth().refresh`). */
  const refresh = useCallback(() => refreshOnce(true), [refreshOnce]);

  /**
   * Resolve auth state using a 3-tier strategy:
   *
   * 1. Read animas_auth cookie (15 min TTL, JS-readable) — instant, no network
   * 2. Call /api/auth/me (uses hub_session, 30 min inactivity) — cross-origin fetch
   * 3. POST /api/auth/unified-refresh (uses animas_refresh, 7 day TTL) — last resort
   *
   * Without tier 3, users idle >30 min get redirected to login even though their
   * 7-day refresh token is still valid. This was the root cause of finance/design
   * shell redirects (Bug #6, 2026-04-02).
   */
  const fetchAuthState = useCallback(async (): Promise<AuthUser | null> => {
    const client = getClient();
    try {
      // Tier 1: Read animas_auth JWT directly from cookie.
      // Fastest path — no network round-trip. Works when token is <15 min old.
      const cookieUser = tryDecodeCookie();
      if (cookieUser) {
        client.setUser(cookieUser);
        setIsLoading(false);
        return cookieUser;
      }

      // Tier 2: Call Portal /api/auth/me cross-origin.
      // Uses hub_session cookie (30 min inactivity timeout).
      // Requires CORS headers on the portal (see corsAuthMiddleware in middleware.ts).
      //
      // IMPORTANT: Tier 2 authenticates the UI but does NOT issue a fresh animas_auth
      // cookie. Without that cookie, API calls to product backends (ops-api, crm-api, etc.)
      // all fail with 401 "Missing authorization token". We fix this by triggering Tier 3
      // in the background immediately after Tier 2 succeeds — the user sees the app
      // instantly while the cookie refresh happens in parallel.
      try {
        const meUser = await tryMeEndpoint();
        if (meUser) {
          client.setUser(meUser);
          setIsLoading(false);
          // Trigger cookie refresh in background — do NOT await, so we don't delay UI.
          // The refresh writes a fresh animas_auth cookie (domain=.animasmarketing.com).
          // Without this, product API calls fail with 401 for up to 12 min until the
          // scheduled refresh timer fires. It shares the single-flight refresh, so a
          // concurrent timer/consumer refresh costs no extra request. On a transient
          // failure it retries in 60 seconds; on rejection (animas_refresh expired)
          // the user stays authenticated via Tier 2 for the UI, but API calls will
          // 401 until they re-login.
          void refreshOnce(false);
          return meUser;
        }
      } catch (err) {
        // TypeError typically means CORS block or network failure.
        if (err instanceof TypeError) {
          const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
          const portalOrigin = configRef.current.portalApiUrl;
          if (currentOrigin && portalOrigin && !portalOrigin.startsWith(currentOrigin)) {
            console.warn(
              "[AuthProvider] Cross-origin /api/auth/me failed — trying token refresh.\n" +
                `  Shell origin: ${currentOrigin}\n` +
                `  Portal origin: ${portalOrigin}\n` +
                "  Error:",
              err
            );
          }
        }
        // Fall through to tier 3
      }

      // Tier 3: Silent token refresh using animas_refresh cookie (7-day TTL).
      // The refresh endpoint is public (no session required) and returns fresh
      // animas_auth + animas_refresh cookies. This is the critical path that
      // prevents unnecessary login redirects when:
      //   - animas_auth expired (>15 min since login/last refresh)
      //   - hub_session expired (>30 min inactivity on portal)
      //   - animas_refresh still valid (within 7 days of login)
      const outcome = await client.refresh();
      if (outcome.status === "refreshed" && outcome.userResolved && outcome.user) {
        setIsLoading(false);
        return outcome.user;
      }

      // All three tiers failed — user is not authenticated
      client.setUser(null);
      setIsLoading(false);
      return null;
    } catch {
      client.setUser(null);
      setIsLoading(false);
      return null;
    }
  }, [getClient, tryDecodeCookie, tryMeEndpoint, refreshOnce]);

  // Schedule the next refresh
  const scheduleRefresh = useCallback(
    (expiryTimestamp?: number) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      // Calculate delay: refresh `refreshThreshold` seconds before expiry
      let delayMs: number;
      if (expiryTimestamp) {
        const now = Math.floor(Date.now() / 1000);
        const secondsUntilExpiry = expiryTimestamp - now;
        delayMs = Math.max(
          (secondsUntilExpiry - refreshThreshold) * 1000,
          10_000 // At least 10 seconds
        );
      } else {
        // No expiry info — refresh every 12 minutes (conservative for 15-min tokens)
        delayMs = 12 * 60 * 1000;
      }

      refreshTimerRef.current = setTimeout(() => {
        refresh();
      }, delayMs);
    },
    [refresh, refreshThreshold]
  );

  // Keep ref in sync so refresh() callback can schedule without circular deps
  scheduleRefreshRef.current = scheduleRefresh;

  // Org switch
  const switchOrg = useCallback(
    async (orgId: number) => {
      // ssrf-ok: browser-side client; URL is configRef.portalApiUrl (own portal API base) + fixed route
      const res = await fetch(`${configRef.current.portalApiUrl}/api/auth/org-switch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ org_id: orgId }),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as ErrorResponse;
        throw new Error(data.error ?? "Failed to switch organization");
      }

      // Re-fetch auth state with new org context
      await fetchAuthState();
    },
    [fetchAuthState]
  );

  // Sign out
  const signOut = useCallback(() => {
    clearTimers();

    // Clear synchronously, then tell the other tabs (they go to the login screen).
    const client = getClient();
    client.setUser(null);
    void client.clearSession();

    // Redirect to Portal logout which clears all cookies
    window.location.href = configRef.current.logoutUrl;
  }, [clearTimers, getClient]);

  // Initialize auth state on mount (skip in E2E bypass — user is pre-set)
  useEffect(() => {
    if (bypassAtInit) return;

    fetchAuthState().then((authUser) => {
      if (authUser) {
        // Use the token's actual expiry for accurate refresh scheduling.
        // Without this, the 12-min default fires AFTER expiry if the token has
        // less than 12 min remaining when the shell loads.
        const tokenExpiry = (authUser as UserWithExpiry)._tokenExpiry;
        scheduleRefresh(tokenExpiry);
      }
    });

    return () => {
      clearTimers();
    };
    // bypassAtInit is derived from isE2EAuthBypass() — stable per session, never changes across renders.
  }, [bypassAtInit, fetchAuthState, scheduleRefresh, clearTimers]);

  // Release the cross-tab channel on unmount (kept separate from the init effect
  // so a refreshThreshold change re-initialises without tearing the client down).
  useEffect(
    () => () => {
      clientRef.current?.dispose();
      clientRef.current = null;
    },
    []
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      switchOrg,
      signOut,
      refresh,
    }),
    [user, isLoading, switchOrg, signOut, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
